<template>
    <div class="w-full">
        <!-- opened from a Service & Repair job ("Universal Quotes" action): "Back to Service & Repair <id>", same pill and label as BackToDetail -->
        <router-link v-if="bookingRoute" v-slot="{ href }" :to="bookingRoute" custom>
            <a :href="href"
                class="inline-flex items-center gap-2 mb-[30px] px-[14px] py-[6px] rounded-full font-roboto text-[14px] text-[#8479E1] no-underline bg-[#fff] shadow-[0_0_10px_0_#0000001A] hover:bg-[#F3F0FF] transition-colors"
                @click="backToBooking($event, bookingRoute)">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                Back to Service & Repair {{ sourceFilter.source_id }}
            </a>
        </router-link>
        <div
            class="relative border-1 rounded-2xl min-w-full min-h-[60vh] 2xl:min-h-[80vh] flex flex-col gap-2 px-2 pt-4 pb-5 border-gray-300">
            <div
                class="w-full flex laptop:justify-end laptop:items-center gap-2 mobile:px-1.5 laptop:px-2.5 -mt-[45px]">
                <div class="mobile:w-full laptop:w-[65%] largeDesktop:w-[40%] flex items-center justify-end gap-2">
                    <!-- Figma (181:949) omits "Show entries", but QA asked for the same pagination on every listing:
                         kept like Appointments / Service & Repair / Replacement Quote so the tabs match -->
                    <div class="mobile:hidden laptop:block">
                        <entities :model-value="limit" global-entities-class="" @update:model-value="onLimitChange" />
                    </div>
                    <searchInput v-model="search" :global-search-class="'mobile:w-full laptop:!w-[55%]'"
                        :input-class="'rounded-full shadow-[0px_0px_10px_0px_#00000026] pl-3 pr-16 py-3 w-full h-10 bg-white disabled:!bg-white disabled:opacity-100 focus:outline-none text-sm'"
                        :buttonclass="'absolute top-1.5 right-1 w-7 h-7'"
                        :img-src="require('@/assets/images/search-icon.png')"
                        :clear-class="'absolute top-0 flex items-center justify-center !h-[100%] right-[2.2rem] laptop:w-4 laptop:h-4 largeDesktop:w-6 largeDesktop:h-6'"
                        :on-clear="onClearSearch" :readonly="loading" :clearable="true" @input="onSearch" />
                    <!-- "+": the same create flow as Create Quote → Universal Quote in the header (globaldashboardHead "fx-create");
                         hidden on an empty list, where the empty state has its own "+ Create" button -->
                    <button v-if="tableItems?.length" type="button" aria-label="Create Universal Quote"
                        class="min-w-[40px] min-h-[40px] max-w-[40px] max-h-[40px] p-0 border-0 bg-transparent cursor-pointer"
                        @click="createNew(3)">
                        <img src="../../assets/images/add-iconRQ.png" alt="" class="w-full h-full object-contain">
                    </button>
                    <!-- list filters (count badge, filled while applied), last in the toolbar -->
                    <ListFilterButton />
                </div>
            </div>
            <!-- cards below 1280px: next to the side menu the table is wider than the page at 1024 (QA D-28) -->
            <div class="desktop:hidden block">
                <div v-if="tableItems.length > 0" class="mt-2">
                    <universalQuotesListing v-for="(item, index) in tableItems" :key="index" :item="item" />
                </div>
                <div v-else>
                    <EmptyState v-if="tableItems == null || (tableItems && tableItems.length == 0)" :text="emptyText || undefined" min-height="min-h-[60vh]">
                        <button type="button" class="fxBtnPrimary mt-[8px]" @click="createNew(3)">+ Create Universal Quote</button>
                    </EmptyState>
                </div>
            </div>
            <div class="mt-2 desktop:block mobile:hidden">
                <universalQuotesTable :table-headers="tableHeaders" :table-data="tableItems"
                    :sort-clicked="sortClicked" :empty-text="emptyText">
                    <template #empty><button type="button" class="fxBtnPrimary mt-[8px]" @click="createNew(3)">+ Create Universal Quote</button></template>
                </universalQuotesTable>
            </div>
        </div>
        <div v-if="tableItems && tableItems.length > 0"
            class="flex mobile:flex-col tablet:flex-row mobile:gap-3 tablet:gap-0 items-center justify-between relative -top-5 px-3">
            <paginationEntries class="mobile:hidden tablet:block" :current-page="page" :per-page="limit"
                :total-items="totalItems" />
            <customPagination :page="page" :total_pages="total_pages" :update-page="updatePage" />
        </div>
    </div>

</template>

<script>
import ListFilterButton from "@/components/partialComponent/ListFilterButton.vue";
import EmptyState from "@/components/partialComponent/EmptyState.vue";
import { mapActions, mapGetters } from "vuex";
import entities from "../../components/showEntities/entities.vue";
import searchInput from '../../components/partialComponent/customInput.vue';
import universalQuotesTable from '../../components/workOrder/universalQuotesTable.vue';
import universalQuotesListing from '../../components/workOrder/universalQuotesListing.vue';
import customPagination from "../../components/partialComponent/customPagination.vue";
import paginationEntries from "../../components/partialComponent/pagination-entries.vue";
import listQueryState from "../../helpers/listQueryState";
import { goBackTo } from "../../helpers/historyPositions";
import { isNewTabClick } from "../../helpers/newTabClick";
import { bookingActionsApi } from "../../services/bookingActionsApi";
import { useToast } from "vue-toastification";
const toast = useToast();

export default {
    components: {
        EmptyState,
        ListFilterButton,
        entities,
        searchInput,
        universalQuotesTable,
        universalQuotesListing,
        customPagination,
        paginationEntries
    },
    mixins: [listQueryState],
    data() {
        return {
            limit: 25,
            search: "",
            page: 1,
            total_pages: 0,
            totalItems: 0,
            debounceTimer: null,
            loading: false,
            tableHeaders: [],
            tableItems: [],
            selectedSort: {
                "column": "id",
                "direction": "DESC"
            },
        }
    },
    computed: {
        ...mapGetters([
            "universalQuotes",
        ]),
        // Android IndependentQuoteListingFragment opened from a job ("Universal Quotes"): only that job's quotes
        sourceFilter() {
            const { source_id, source_type } = this.$route.query;
            return source_id && source_type ? { source_id, source_type } : {};
        },
        bookingRoute() {
            return this.sourceFilter.source_type === "sar" ? `/job/detail/${this.sourceFilter.source_id}` : null;
        },
        emptyText() {
            return this.sourceFilter.source_type === "sar"
                ? "No Universal Quotes to display for this Service & Repair."
                : ""; // EmptyState's generic text
        },
    },
    watch: {
        universalQuotes: function (data) {
            this.loading = false
            this.tableItems = this.universalQuotes.body
            this.total_pages = this.universalQuotes.paging.total_pages
            this.totalItems = this.universalQuotes.paging.total_records
            this.fixListPage()
        },
    },
    mounted() {
        this.setTableHeaders();
        this.restoreListState();
        this.fetchList();
    },
    methods: {
        // opened from an S&R job (?source_type=sar): like Android, create for that job's consumer and source and go
        // straight to the quote config step; otherwise the header's Create Universal Quote flow
        async createNew(item) {
            let detail = item;
            if (this.sourceFilter.source_type === "sar") {
                const res = await bookingActionsApi.show(this.sourceFilter.source_id);
                const job = res.ok ? res.body : null;
                if (!job?.consumer || !Number(job.company_consumer_id)) {
                    toast.error(res.message || "Consumer does not exist.");
                    return;
                }
                const c = job.consumer;
                detail = {
                    item,
                    preset: {
                        consumer_id: job.company_consumer_id,
                        user_id: c.user_id,
                        source_type: "service-and-repair",
                        source_id: Number(this.sourceFilter.source_id),
                        address_id: null,
                        address: null,
                        phone: c.phone || "",
                        email: c.email || "",
                        first_name: c.first_name || "",
                        last_name: c.last_name || "",
                    },
                };
            }
            window.dispatchEvent(new CustomEvent("fx-create", { detail }));
        },
        // back to the booking's history entry (no new one); Ctrl/Cmd/middle click still opens it in a new tab
        backToBooking(event, to) {
            if (isNewTabClick(event)) return;
            event.preventDefault();
            goBackTo(this.$router, to);
        },
        ...mapActions([
            "getuniversalQuotes",
        ]),
        // every list load goes through here (keeps the sort and saves the state in the URL; source_id/source_type stay)
        fetchList() {
            this.loading = true
            this.getuniversalQuotes({
                ...this.sourceFilter,
                search: this.search,
                limit: this.limit,
                page: this.page,
                sort: this.selectedSort,
                ...this.listFilterParams,
            });
            this.saveListState()
        },
        updatePage(page) {
            if (page == this.page) return
            this.page = page
            this.fetchList()
            this.$nextTick(() => {
                document.documentElement.scrollTo({
                    top: 0,
                    behavior: "smooth"
                });
            });
        },
        onSearch() {
            clearTimeout(this.debounceTimer);
            this.page = 1
            this.debounceTimer = setTimeout(() => {
                if (this.search.length == 0 || this.search.trim().length > 0) {
                    this.fetchList()
                }
            }, 1000);
        },
        onClearSearch() {
            clearTimeout(this.debounceTimer);
            this.page = 1
            this.search = ''
            this.fetchList()
        },
        onLimitChange(newLimit) {
            this.limit = newLimit;
            this.page = 1;
            this.fetchList();
        },
        setTableHeaders() {
            this.tableHeaders = [
                this.getTableHeaderObject("Quote ID", "id", "id", "", "1"),
                this.getTableHeaderObject("Created Date", "created_at", "created_at", "", "1"),
                this.getTableHeaderObject("Consumer Name", "full_name", "full_name", "", "1"),
                this.getTableHeaderObject("Job Type", "title", "title", "", "1"),
                this.getTableHeaderObject("Source", "Source", "Source", "", "1"),
                this.getTableHeaderObject("Amount", "selected_payment", "selected_payment", "", "1"),
                this.getTableHeaderObject("Status", "status_text", "status_text", "", ""),
            ]
            this.setSort()
        },
        getTableHeaderObject(title, data_value, sort_column, sort_direction, sort_applicable, isHeaderToolTipShow = 0, tooltip_text = '') {
            return {
                title: title,
                data_value: data_value,
                sort_column: sort_column,
                sort_direction: sort_direction,
                sort_applicable: sort_applicable,
                isHeaderToolTipShow: isHeaderToolTipShow,
                tooltip_text: tooltip_text
            }
        },
        setSort() {
            this.tableHeaders.map(obj => {
                if (obj.sort_column == this.selectedSort.column) {
                    obj.sort_direction = this.selectedSort.direction;
                } else {
                    obj.sort_direction = "";
                }
            });
        },
        sortClicked(item) {
            if (item.sort_applicable == '1') {
                this.page = 1
                if (item.sort_column == this.selectedSort.column) {
                    this.selectedSort.direction = item.sort_direction == "DESC" ? "ASC" : "DESC"
                    item.sort_direction = this.selectedSort.direction
                } else {
                    this.tableHeaders.map(obj => {
                        obj.sort_direction = "";
                    });
                    this.selectedSort.column = item.sort_column
                    this.selectedSort.direction = item.sort_direction == "DESC" ? "ASC" : "DESC"
                    item.sort_direction = this.selectedSort.direction
                }
                this.fetchList()
            }
        },
    },
};
</script>